/**
 * 사업장 현황의 규칙 — 화면을 **지역 › 사업장 › 위치** 세 층으로 묶는다(모니터링 메뉴). 왼쪽 나무는 맨 위의 조직 이름
 * 아래 지역 › 사업장 › 화면, 오른쪽 그림은 고른 범위(전체·지역·사업장·화면)를 영역과 화면 노드로 그린다(`SiteScope`).
 *
 * **층을 부르는 말은 고객사가 고른다**(2026-10-26, `siteLabels.ts`) — 제조면 지역 › 공장 › 건물, 유통이면 권역 › 매장 ›
 * 구역. 이 파일의 이름(`FactoryNode`·`BuildingNode`, 아래 주석의 "공장·건물")은 그 전에 붙인 것이라 둘째 층·셋째 층을
 * 뜻한다. 정하지 않은 지역·위치의 이름("지역 미지정")만 고른 말을 따른다(`placeOf`·`buildSiteTree` 의 `labels`).
 *
 * - 건물은 화면의 위치다. 이 서버의 위치별 집계(`/displays/summary?locations=1`)와, 통합 관제에 등록한 다른
 *   서버마다 수집기가 30초마다 받아 둔 위치별 집계를 같은 모양(출처 `SiteSource`)으로 받는다.
 * - 지역·공장은 사업장 구성(`/api/v1/sites`)의 줄이 정한다 — 그 건물의 줄이 있으면 그것, 없으면 그 출처의 기본
 *   줄(`location: null`), 그것도 없으면 지역은 "지역 미지정", 공장은 출처 이름(본 서버면 "본 서버").
 *   칸마다 따로 내려간다 — 건물 줄에 지역만 적었으면 공장은 기본 줄의 것.
 * - 연결이 끊긴 서버의 숫자는 끊기기 전의 것이다. 버리지 않고 남기되, 그 공장에 끊김 표시를 단다(`stale`).
 *   화면 장애로 세지 않는다 — 통합 관제와 같은 규칙.
 * - **주소**(2026-10-22)도 같은 줄에 단다 — 건물 줄의 주소가 있으면 그것, 없으면 그 출처의 기본 주소. 주소의 좌표로
 *   모니터링 지도에 놓는다: 건물은 자기 좌표, 공장은 그 건물들 좌표의 가운데(`FactoryNode.point`). 좌표가 하나도 없는
 *   공장은 지도에 서지 않고 "주소 없는 공장" 으로 따로 센다.
 */
import type { HubDisplay, HubServerView, HubState, SiteAddress, SiteEntry, WallCard } from './api/client.js';
import { compareLocationNames } from './components/locationOptions.js';
import { displayLive, liveStatus } from './components/displayLive.js';
import { DEFAULT_SITE_LABELS, unassignedLocation, unnamedRegion, type SiteLabels } from './siteLabels.js';

export const LOCAL_SOURCE = 'local';
/** 기본 층 이름일 때의 "지역 미지정" — 고른 말이 있으면 `unnamedRegion(labels)`. */
export const UNNAMED_REGION = unnamedRegion(DEFAULT_SITE_LABELS);
export const LOCAL_SITE_NAME = '본 서버';
/** 기본 층 이름일 때의 "위치 미지정" — 고른 말이 있으면 `unassignedLocation(labels)`. */
export const UNASSIGNED_BUILDING = unassignedLocation(DEFAULT_SITE_LABELS);
/** 조직 이름을 정하지 않았을 때 나무 맨 위·범위 줄의 첫 칸. */
export const ALL_SITES = '전체';

/** 지도 위의 한 점 — WGS84 위도·경도. */
export interface GeoPoint {
  lat: number;
  lng: number;
}

/** 주소를 적었는가 — 기본 주소 글자나 좌표 중 하나라도 있으면. 빈 덩어리는 "적지 않음" 이다(기본값을 따른다). */
export function hasAddress(address: SiteAddress | null | undefined): address is SiteAddress {
  return Boolean(address && (address.address.trim() || pointOf(address)));
}

/** 주소의 좌표 — 둘 다 있고 범위 안일 때만. */
export function pointOf(address: SiteAddress | null | undefined): GeoPoint | null {
  if (!address) return null;
  const { latitude: lat, longitude: lng } = address;
  if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return { lat, lng };
}

/** 한 줄로 — '경기도 평택시 … 12 (2층)'. 없으면 빈 문자열. */
export function addressLine(address: SiteAddress | null | undefined): string {
  if (!address) return '';
  const base = address.address.trim();
  const detail = address.address_detail.trim();
  return detail ? `${base} ${detail}`.trim() : base;
}

/** 점들의 가운데 — 같은 공장의 건물들은 가까우므로 평균으로 충분하다. 없으면 null. */
export function centerOf(points: readonly GeoPoint[]): GeoPoint | null {
  if (points.length === 0) return null;
  const lat = points.reduce((sum, point) => sum + point.lat, 0) / points.length;
  const lng = points.reduce((sum, point) => sum + point.lng, 0) / points.length;
  return { lat, lng };
}

/** 나무 맨 위에 적을 조직 이름 — 사업장 구성의 `name`(사업장·위치 메뉴에서 정한다), 비면 "전체". */
export function siteTitle(name: string | null | undefined): string {
  return name?.trim() || ALL_SITES;
}

export interface SiteSource {
  /** `local` 또는 통합 관제 서버 id. */
  id: string;
  name: string;
  kind: 'local' | 'remote';
  /** 다른 서버의 연결 상태. 이 서버는 늘 `ok`. */
  state: HubState | 'ok';
  /** 끊기기 전의 숫자다(세 주기 넘게 못 받았다). */
  stale: boolean;
  /** 숫자를 센 시각. */
  asOf: string | null;
  locations: Array<{ name: string; total: number; online: number; offline: number }>;
  /**
   * 화면 목록 — 받았으면 건물마다 화면 노드를 그리고 숫자도 이것으로 센다(노드와 숫자가 어긋나지 않게).
   * 없으면(`null`·끊긴 서버·아직 못 받음) 숫자는 위치별 집계로, 노드는 그리지 못한다.
   */
  displays?: SiteDisplayInput[] | null;
  /** 목록이 다 오지 않았다(상한에 걸림) — 숫자는 집계로 세고 노드는 받은 것만 그린다. */
  truncated?: boolean;
}

/** 출처가 준 화면 한 대 — 본 서버는 화면 그림 목록(`/displays/wall`)의 한 장(썸네일을 그린다), 원격 서버는 통합 관제의 목록 한 줄. */
export interface SiteDisplayInput {
  id: string;
  name: string;
  location: string;
  online: boolean;
  lastSeen: string | null;
  version: string;
  error: string | null;
  /** 기기가 보고한 MAC — 모르면 null. */
  mac: string | null;
  card: WallCard | null;
}

export interface SiteDisplay extends SiteDisplayInput {
  /** `출처:id` — 주소(`?display=`)에 그대로 싣는다. */
  key: string;
  source: SiteSource;
  /** 보이는 건물 이름(위치가 비었으면 "위치 미지정"). */
  building: string;
}

export function localDisplay(card: WallCard): SiteDisplayInput {
  return {
    id: card.id,
    name: card.name,
    location: card.location ?? '',
    online: card.online,
    lastSeen: card.status.last_heartbeat_at,
    version: card.status.app_version ?? '',
    // 끊긴 화면은 오프라인 하나만 — 마지막 보고에 남은 오류("Failed to fetch" 등)는 지금 상태가 아니다(`displayLive`).
    error: displayLive(card).error,
    mac: card.status.mac ?? null,
    card,
  };
}

export function remoteDisplay(display: HubDisplay): SiteDisplayInput {
  return {
    id: display.id,
    name: display.name,
    location: display.location ?? '',
    online: display.online,
    lastSeen: display.last_heartbeat_at,
    version: display.app_version,
    error: liveStatus(display.online, display.maintenance, display.last_error).error,
    mac: display.mac ?? null,
    card: null,
  };
}

export interface Counts {
  total: number;
  online: number;
  offline: number;
}

export interface BuildingNode extends Counts {
  key: string;
  /** 보이는 이름 — 위치가 비었으면 "위치 미지정". */
  name: string;
  /** 그 출처에서의 위치 값 그대로(빈 문자열은 미지정) — 화면 목록을 거를 때 쓴다. */
  location: string;
  source: SiteSource;
  /** 이 건물의 화면 — 출처의 목록을 받지 못했으면 `null`. 꺼진 화면이 앞이다. */
  displays: SiteDisplay[] | null;
  /** 이 건물의 주소 — 건물 줄의 것, 없으면 출처 기본 주소. 둘 다 없으면 null. */
  address: SiteAddress | null;
  /** 지도에 놓을 자리 — 주소의 좌표. */
  point: GeoPoint | null;
}

export interface FactoryNode extends Counts {
  key: string;
  name: string;
  region: string;
  buildings: BuildingNode[];
  /** 이 공장에 화면을 대는 출처들 — 대개 하나(서버 하나가 공장 하나). */
  sources: SiteSource[];
  /** 출처 중 하나라도 끊겼다 — 숫자 일부가 끊기기 전의 것이다. */
  stale: boolean;
  /** 지도에 놓을 자리 — 좌표가 있는 건물들의 가운데. 하나도 없으면 null(지도에 서지 않는다). */
  point: GeoPoint | null;
}

export interface RegionNode extends Counts {
  key: string;
  name: string;
  factories: FactoryNode[];
  stale: boolean;
}

/** 다른 서버 하나를 출처로 — 요약을 아직 못 받았으면 위치가 없다. */
export function remoteSource(server: HubServerView): SiteSource {
  return {
    id: server.id,
    name: server.name,
    kind: 'remote',
    state: server.state,
    stale: server.stale || server.state !== 'ok',
    asOf: server.summary?.as_of ?? server.last_ok_at,
    locations: server.summary?.locations ?? [],
    displays: null,
  };
}

/** 이 출처의 이 위치가 놓일 지역·사업장·주소. 빈 칸은 위로 내려가며 찾는다. 정하지 않은 지역은 고른 말로("권역 미지정"). */
export function placeOf(
  source: Pick<SiteSource, 'id' | 'kind' | 'name'>,
  location: string,
  entries: readonly SiteEntry[],
  labels: SiteLabels = DEFAULT_SITE_LABELS,
): { region: string; site: string; address: SiteAddress | null } {
  const own = entries.find((entry) => entry.source === source.id && entry.location === location);
  const base = entries.find((entry) => entry.source === source.id && entry.location === null);
  const region = own?.region || base?.region || '';
  const site = own?.site || base?.site || (source.kind === 'local' ? LOCAL_SITE_NAME : source.name);
  const address = hasAddress(own?.address) ? own.address : hasAddress(base?.address) ? base.address : null;
  return { region: region || unnamedRegion(labels), site, address };
}

const add = (into: Counts, from: Counts): void => {
  into.total += from.total;
  into.online += from.online;
  into.offline += from.offline;
};

/** 이름순, 다만 "…미지정" 은 맨 뒤 — 정리가 안 된 것이 앞에 서서 정리된 현장을 밀어내지 않게. */
function byName(a: { name: string }, b: { name: string }, unnamed: string): number {
  if (a.name === unnamed && b.name !== unnamed) return 1;
  if (b.name === unnamed && a.name !== unnamed) return -1;
  return compareLocationNames(a.name, b.name);
}

/** 목록으로 위치별 숫자를 센다 — 집계와 같은 모양. */
function countByLocation(displays: readonly SiteDisplayInput[]): SiteSource['locations'] {
  const byName = new Map<string, { name: string; total: number; online: number; offline: number }>();
  for (const display of displays) {
    const row = byName.get(display.location) ?? { name: display.location, total: 0, online: 0, offline: 0 };
    row.total += 1;
    if (display.online) row.online += 1;
    else row.offline += 1;
    byName.set(display.location, row);
  }
  return [...byName.values()];
}

/** 꺼진 화면이 앞, 그다음 이름순 — 노드를 훑는 눈이 문제부터 닿게. */
function byStateThenName(a: SiteDisplay, b: SiteDisplay): number {
  return Number(a.online) - Number(b.online) || a.name.localeCompare(b.name, 'ko');
}

export function buildSiteTree(
  sources: readonly SiteSource[],
  entries: readonly SiteEntry[],
  labels: SiteLabels = DEFAULT_SITE_LABELS,
): RegionNode[] {
  const regions = new Map<string, RegionNode>();
  const unassigned = unassignedLocation(labels);
  for (const source of sources) {
    const listed = source.displays ?? null;
    const locations = listed && !source.truncated ? countByLocation(listed) : source.locations;
    for (const location of locations) {
      if (location.total === 0) continue;
      const { region: regionName, site, address } = placeOf(source, location.name, entries, labels);
      let region = regions.get(regionName);
      if (!region) {
        region = { key: regionName, name: regionName, factories: [], total: 0, online: 0, offline: 0, stale: false };
        regions.set(regionName, region);
      }
      let factory = region.factories.find((item) => item.name === site);
      if (!factory) {
        factory = { key: `${regionName}\u0000${site}`, name: site, region: regionName, buildings: [], sources: [], total: 0, online: 0, offline: 0, stale: false, point: null };
        region.factories.push(factory);
      }
      if (!factory.sources.includes(source)) factory.sources.push(source);
      factory.stale ||= source.stale;
      region.stale ||= source.stale;
      const buildingName = location.name || unassigned;
      const building: BuildingNode = {
        key: `${source.id}\u0000${location.name}`,
        name: buildingName,
        location: location.name,
        source,
        total: location.total,
        online: location.online,
        offline: location.offline,
        displays: listed
          ? listed
              .filter((display) => display.location === location.name)
              .map((display) => ({ ...display, key: `${source.id}:${display.id}`, source, building: buildingName }))
              .sort(byStateThenName)
          : null,
        address,
        point: pointOf(address),
      };
      factory.buildings.push(building);
      add(factory, building);
      add(region, building);
    }
  }
  const sorted = [...regions.values()].sort((a, b) => byName(a, b, unnamedRegion(labels)));
  for (const region of sorted) {
    region.factories.sort((a, b) => compareLocationNames(a.name, b.name));
    for (const factory of region.factories) {
      factory.buildings.sort((a, b) => byName(a, b, unassigned));
      factory.point = centerOf(factory.buildings.flatMap((building) => (building.point ? [building.point] : [])));
    }
  }
  return sorted;
}

/** 공장의 화면 전부 — 건물 순서대로, 건물 안에서는 꺼진 것이 앞. 목록을 못 받은 건물은 빠진다. */
export function factoryDisplays(factory: FactoryNode): SiteDisplay[] {
  return factory.buildings.flatMap((building) => building.displays ?? []);
}

/**
 * 그림이 그릴 범위 — 나무에서 고른 것. 주소에 남겨(`?region=&site=&display=`) 새로 고쳐도·링크로 보내도 같은
 * 범위가 열린다. 이름이 바뀌어 더 없는 범위는 전체로 돌아간다(`resolveScope`).
 */
export type SiteScope =
  | { kind: 'all' }
  | { kind: 'region'; region: string }
  | { kind: 'factory'; region: string; site: string }
  | { kind: 'display'; region: string; site: string; display: string };

export function scopeFromQuery(get: (key: string) => string | null): SiteScope {
  const region = get('region');
  const site = get('site');
  const display = get('display');
  if (region === null) return { kind: 'all' };
  if (site === null) return { kind: 'region', region };
  if (display === null) return { kind: 'factory', region, site };
  return { kind: 'display', region, site, display };
}

export function scopeQuery(scope: SiteScope): { region: string | null; site: string | null; display: string | null } {
  return {
    region: scope.kind === 'all' ? null : scope.region,
    site: scope.kind === 'factory' || scope.kind === 'display' ? scope.site : null,
    display: scope.kind === 'display' ? scope.display : null,
  };
}

export interface ResolvedScope {
  scope: SiteScope;
  region: RegionNode | null;
  factory: FactoryNode | null;
  display: SiteDisplay | null;
}

/** 고른 범위를 지금의 나무에서 찾는다 — 없어진 층에서 멈추고 그 위의 범위로 돌아간다. */
export function resolveScope(tree: readonly RegionNode[], scope: SiteScope): ResolvedScope {
  const none: ResolvedScope = { scope: { kind: 'all' }, region: null, factory: null, display: null };
  if (scope.kind === 'all') return none;
  const region = tree.find((item) => item.name === scope.region);
  if (!region) return none;
  const atRegion: ResolvedScope = { scope: { kind: 'region', region: region.name }, region, factory: null, display: null };
  if (scope.kind === 'region') return atRegion;
  const factory = region.factories.find((item) => item.name === scope.site);
  if (!factory) return atRegion;
  const atFactory: ResolvedScope = { scope: { kind: 'factory', region: region.name, site: factory.name }, region, factory, display: null };
  if (scope.kind === 'factory') return atFactory;
  const display = factoryDisplays(factory).find((item) => item.key === scope.display);
  if (!display) return atFactory;
  return { scope, region, factory, display };
}

/**
 * 모니터링 보기 — 그림판(`/wall`, 이 서버만)과 통합 모니터링(`/integrated`, 지도, 연동된 원격 서버까지). 2026-10-28 부터 메뉴가
 * 나뉘어 보기는 경로가 정한다(예전의 `?view=map` 전환은 `/integrated` 로 옮겨 준다 — `screens/Monitoring.tsx`).
 */
export type SitesView = 'board' | 'map';

/** 꺼진 비율로 색을 고른다 — 다 켜짐 · 일부 꺼짐 · 절반 넘게 꺼짐. 끊긴 출처의 숫자는 회색. */
export function healthTone(counts: Counts, stale = false): 'ok' | 'warn' | 'danger' | 'muted' {
  if (stale) return 'muted';
  if (counts.total === 0 || counts.offline === 0) return 'ok';
  return counts.offline * 2 > counts.total ? 'danger' : 'warn';
}

/** 저장할 주소 — 적지 않았으면 null, 적었으면 칸마다 다듬어서. */
export function storedAddress(address: SiteAddress | null | undefined): SiteAddress | null {
  if (!hasAddress(address)) return null;
  return {
    postal_code: address.postal_code.trim(),
    address: address.address.trim(),
    address_detail: address.address_detail.trim(),
    latitude: pointOf(address)?.lat ?? null,
    longitude: pointOf(address)?.lng ?? null,
  };
}

/** 사업장 구성 표를 저장할 줄로 — 화면의 입력(출처 → 기본값·건물별 지역·공장·주소)을 서버 모양으로 편다. */
export function entriesFromDraft(draft: Record<string, SiteDraft>): SiteEntry[] {
  const out: SiteEntry[] = [];
  for (const [source, value] of Object.entries(draft)) {
    const base = { region: value.region.trim(), site: value.site.trim(), address: storedAddress(value.address) };
    if (base.region || base.site || base.address) out.push({ source, location: null, ...base });
    for (const [location, place] of Object.entries(value.buildings)) {
      const region = place.region.trim();
      const site = place.site.trim();
      const address = storedAddress(place.address);
      if (region || site || address) out.push({ source, location, region, site, address });
    }
  }
  return out;
}

/** 한 칸(출처 기본값 또는 건물 하나)의 지역·공장·주소. 빈 칸·null 은 위의 값을 따른다. */
export interface SitePlaceDraft {
  region: string;
  site: string;
  address: SiteAddress | null;
}

export interface SiteDraft extends SitePlaceDraft {
  buildings: Record<string, SitePlaceDraft>;
}

/** 저장된 줄을 표 입력으로 — 출처마다 기본값과 건물별 칸. 지금 없는 건물의 줄도 남긴다(지우면 사라진다). */
export function draftFromEntries(sources: readonly SiteSource[], entries: readonly SiteEntry[]): Record<string, SiteDraft> {
  const draft: Record<string, SiteDraft> = {};
  for (const source of sources) {
    const base = entries.find((entry) => entry.source === source.id && entry.location === null);
    const buildings: SiteDraft['buildings'] = {};
    for (const location of source.locations) buildings[location.name] = { region: '', site: '', address: null };
    for (const entry of entries) {
      if (entry.source === source.id && entry.location !== null) {
        buildings[entry.location] = { region: entry.region, site: entry.site, address: entry.address ?? null };
      }
    }
    draft[source.id] = { region: base?.region ?? '', site: base?.site ?? '', address: base?.address ?? null, buildings };
  }
  return draft;
}

/** 그 건물 줄에 직접 적은 주소 — 적지 않았으면(기본 주소를 따르는 중이면) null. */
export function ownBuildingAddress(entries: readonly SiteEntry[], source: string, location: string): SiteAddress | null {
  const own = entries.find((entry) => entry.source === source && entry.location === location)?.address;
  return hasAddress(own) ? own : null;
}

/** 그 출처의 기본 주소 — 건물이 주소를 적지 않으면 따르는 것. 없으면 null. */
export function sourceBaseAddress(entries: readonly SiteEntry[], source: string): SiteAddress | null {
  const base = entries.find((entry) => entry.source === source && entry.location === null)?.address;
  return hasAddress(base) ? base : null;
}

/**
 * 건물 하나의 주소만 바꾼 줄 전체 — 모니터링에서 건물 주소를 곧바로 저장할 때(2026-10-22 사용자 지시 "모니터링 화면에서
 * 화면이 위치한 건물의 주소를 저장"). 그 (출처, 위치) 줄이 있으면 주소만 갈고 지역·공장은 그대로, 없으면 지역·공장은
 * 비운 채(기본값을 따른다) 새 줄을 더한다. `null` 은 주소를 지운다 — 지역·공장도 비었으면 줄째 빠진다. 다른 줄은 그대로.
 */
export function withBuildingAddress(
  entries: readonly SiteEntry[],
  source: string,
  location: string,
  address: SiteAddress | null,
): SiteEntry[] {
  const next = storedAddress(address);
  const out: SiteEntry[] = [];
  let found = false;
  for (const entry of entries) {
    if (entry.source !== source || entry.location !== location) {
      out.push(entry);
      continue;
    }
    found = true;
    const changed = { ...entry, address: next };
    if (changed.region.trim() || changed.site.trim() || changed.address) out.push(changed);
  }
  if (!found && next) out.push({ source, location, region: '', site: '', address: next });
  return out;
}

/**
 * 저장할 줄 전체 — **표에 없는 출처의 줄은 그대로 둔다.** 통합 관제를 볼 수 없는 사람이 이 서버의 이름표만
 * 고쳐 저장해도 다른 서버의 지역·공장이 지워지면 안 된다(지워진 서버의 줄도 다시 등록하면 살아난다).
 */
export function mergeSiteEntries(previous: readonly SiteEntry[], draft: Record<string, SiteDraft>): SiteEntry[] {
  return [...previous.filter((entry) => !(entry.source in draft)), ...entriesFromDraft(draft)];
}
